import { useEffect, useState, useCallback } from 'react'
import {
  apiAgentGetMembers,
  apiAgentImpersonate,
  apiAgentUpdateMemberStatus,
  ApiRequestError,
  type AdminMember,
} from '@/lib/api'
import type { PaginatedResult } from '@nexbit/types'
import { format } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import { AlertCircle, ChevronLeft, ChevronRight, Grid3x3, Loader2, LogIn, ShieldOff, ShieldCheck, Ticket } from 'lucide-react'
import { useT } from '@/contexts/language'

const MEMBER_APP_URL = (import.meta.env.VITE_MEMBER_APP_URL as string | undefined) ?? 'http://localhost:3000'
// Empty when the environment has no KENO site yet (prod today): "เข้าในนาม" then goes
// straight to the lottery, as it always did, instead of offering a choice that dead-ends.
const KENO_APP_URL = (import.meta.env.VITE_KENO_APP_URL as string | undefined) || ''

type ImpersonateTarget = 'lottery' | 'keno'

/**
 * Where a redeemed impersonation token is sent. Each site redeems it its own way: the
 * member site in a route handler that sets the session cookie, KENO in the page, which
 * keeps the session in sessionStorage. The token is single-use and lives 60 seconds.
 */
function impersonateUrl(target: ImpersonateTarget, token: string): string {
  const t = encodeURIComponent(token)
  return target === 'keno'
    ? `${KENO_APP_URL}/impersonate?token=${t}`
    : `${MEMBER_APP_URL}/api/auth/impersonate?token=${t}`
}

function statusVariant(s: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (s === 'active')    return 'success'
  if (s === 'suspended') return 'destructive'
  if (s === 'inactive')  return 'warning'
  return 'outline'
}

export default function AgentMembersPage() {
  const t = useT()
  const [data, setData] = useState<PaginatedResult<AdminMember> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null)
  const [suspendingId, setSuspendingId] = useState<string | null>(null)

  const load = useCallback(async (p: number) => {
    setLoading(true)
    setError(null)
    try {
      setData(await apiAgentGetMembers(p, 20))
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load(page) }, [load, page])

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0

  async function handleToggleSuspend(m: AdminMember) {
    setSuspendingId(m.id)
    try {
      const newStatus = m.status === 'active' ? 'suspended' : 'active'
      await apiAgentUpdateMemberStatus(m.id, newStatus)
      setData((d) => d ? { ...d, items: d.items.map((x) => x.id === m.id ? { ...x, status: newStatus } : x) } : d)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.members.suspendError)
    } finally {
      setSuspendingId(null)
    }
  }

  // The member whose "เข้าในนาม" is waiting on a choice of site.
  const [choosing, setChoosing] = useState<AdminMember | null>(null)

  function handleLoginAs(m: AdminMember) {
    if (KENO_APP_URL) setChoosing(m)
    else void handleImpersonate(m, 'lottery')
  }

  // The token is requested only after the choice: it is single-use and dies in 60 seconds,
  // so one minted before the dialog could expire while it stays open.
  async function handleImpersonate(m: AdminMember, target: ImpersonateTarget) {
    setChoosing(null)
    setImpersonatingId(m.id)
    try {
      const { token } = await apiAgentImpersonate(m.id)
      window.open(impersonateUrl(target, token), '_blank', 'noopener')
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.members.impersonateError)
    } finally {
      setImpersonatingId(null)
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.members.title}
        </span>
        <span className="font-mono text-[10px] text-muted-foreground">
          {t.members.createdViaApiNotice}
        </span>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            Member List
          </span>
          {data && !loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">
              {data.total} รายการ
            </span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-14" />
                <Skeleton className="h-5 w-14" />
                <Skeleton className="ml-auto h-4 w-24" />
                <Skeleton className="h-7 w-16" />
              </div>
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center">
            <p className="font-mono text-sm text-muted-foreground">{t.members.noMembers}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.common.username}</TableHead>
                <TableHead>{t.common.status}</TableHead>
                <TableHead className="text-right">{t.common.createdShort}</TableHead>
                <TableHead className="text-right" colSpan={3} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>
                    <div>
                      <p className="font-mono text-sm font-semibold">{m.username}</p>
                      {m.displayName && (
                        <p className="font-mono text-xs text-muted-foreground">{m.displayName}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(m.status)} className="text-[10px]">
                      {m.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <time dateTime={m.createdAt} className="font-mono text-xs text-muted-foreground">
                      {format(new Date(m.createdAt), 'dd/MM/yy')}
                    </time>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 font-mono text-xs gap-1.5"
                      onClick={() => void handleToggleSuspend(m)}
                      disabled={suspendingId === m.id}
                      title={m.status === 'active' ? t.members.suspend : t.members.unsuspend}
                    >
                      {suspendingId === m.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : m.status === 'active' ? (
                        <ShieldOff className="h-3 w-3 text-destructive" />
                      ) : (
                        <ShieldCheck className="h-3 w-3 text-success" />
                      )}
                      {m.status === 'active' ? t.members.suspend : t.members.unsuspend}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 font-mono text-xs gap-1.5 text-primary hover:text-primary hover:bg-primary/10"
                      onClick={() => handleLoginAs(m)}
                      disabled={impersonatingId === m.id}
                      title="เปิด tab ใหม่ในฐานะสมาชิกคนนี้"
                    >
                      {impersonatingId === m.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <LogIn className="h-3 w-3" />
                      )}
                      {t.members.loginAs}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
            <span className="font-mono text-xs text-muted-foreground">
              {page} / {totalPages}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                aria-label="หน้าก่อน"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="หน้าถัดไป"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={choosing !== null} onOpenChange={(open) => { if (!open) setChoosing(null) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{choosing ? t.members.chooseGameTitle(choosing.username) : ''}</DialogTitle>
            <DialogDescription>{t.members.chooseGameDescription}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {([
              ['lottery', Ticket, t.members.gameLottery, t.members.gameLotteryHint],
              ['keno', Grid3x3, t.members.gameKeno, t.members.gameKenoHint],
            ] as const).map(([target, Icon, label, hint]) => (
              <button
                key={target}
                type="button"
                onClick={() => { if (choosing) void handleImpersonate(choosing, target) }}
                className="flex flex-col items-start gap-2 rounded-[var(--radius)] border border-border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:bg-primary/10"
              >
                <Icon className="h-5 w-5 text-primary" aria-hidden />
                <span className="text-sm font-semibold text-foreground">{label}</span>
                <span className="text-xs text-muted-foreground">{hint}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
